import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Filter, Eye, Check, X, Search, ChevronRight, ImageIcon, Download, List, 
  GitCommit, MessageSquare, Send, Clock, User, AlertCircle, CornerUpLeft, 
  FileEdit, Plus, LayoutGrid, Paperclip, Play, FileText as FileIcon, Maximize2,
  MoreHorizontal, Calendar, Box, Tag, Lightbulb
} from 'lucide-react';
import { 
  SubmissionData, Brand, WorkflowLog, 
  User as UserType, Attachment, Campaign 
} from '../../../types';
import { SubmissionStatus, WorkflowStage, UserRole, Department, AdminView, SubmissionType } from '../../../enums';
import { apiClient } from '../../../services/apiClient';
import { asArray } from '../../../utils/asArray';
import { BrandChip } from '../../common/BrandChip';
import { SubmissionCode } from '../../common/SubmissionCode';

interface SubmissionsViewProps {
    submissions: SubmissionData[];
    brands: Brand[];
    campaigns?: Campaign[];
    onUpdateSubmissions: (submissions: SubmissionData[]) => void;
    onAddNotification: (type: 'info' | 'success' | 'warning' | 'error', title: string, message: string, targetView?: AdminView, targetId?: string) => void;
    showToast?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
    currentUser: UserType;
    users: UserType[]; 
    focusSubmissionId?: string | null;
    onNavigate?: (view: AdminView) => void;
                onBack?: () => void;
                currencyCode?: string;
}

export const SubmissionsView: React.FC<SubmissionsViewProps> = ({ 
        submissions, brands, campaigns, onUpdateSubmissions, onAddNotification, showToast, currentUser, users, focusSubmissionId, onNavigate, onBack, currencyCode 
}) => {
    const currency = currencyCode || 'QAR';
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All Statuses');
  const [brandFilter, setBrandFilter] = useState<string>('All Brands');
  const [statusTab, setStatusTab] = useState<'active' | 'completed' | 'rejected'>('active');
  
  // Drawer State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'timeline' | 'overview'>('timeline');
  const [itemsViewMode, setItemsViewMode] = useState<'list' | 'gallery'>('list');
  const [discussingItem, setDiscussingItem] = useState<{ name: string } | null>(null);
  
  // Chat State
  const [commentText, setCommentText] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preview State
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);

  // Action State
  const [confirmingAction, setConfirmingAction] = useState<{ id: string, action: 'Approve' | 'Reject' | 'Return' } | null>(null);
  const [returnNote, setReturnNote] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const submissionsRef = useRef(submissions);

  useEffect(() => {
    submissionsRef.current = submissions;
  }, [submissions]);

  useEffect(() => {
    if (focusSubmissionId) {
      setSelectedSubmissionId(focusSubmissionId);
      setIsDrawerOpen(true);
      setActiveTab('timeline');
    }
  }, [focusSubmissionId]);

  // Helper for Image Resolution
  const resolveImageUrl = (raw: string | undefined | null) => {
    if (!raw) return null;
    if (raw.startsWith('data:')) return raw;
    if (raw.startsWith('/api/assets')) return raw;
    if (raw.includes('.r2.dev/')) return `/api/assets?key=${encodeURIComponent(raw.split('.r2.dev/')[1])}`;
    if (raw.includes('sterlingops-assets/')) return `/api/assets?key=${encodeURIComponent(raw.split('sterlingops-assets/')[1])}`;
    if (raw.startsWith('uploads/')) return `/api/assets?key=${encodeURIComponent(raw)}`;
    if (raw.startsWith('http')) return raw;
    return `data:image/png;base64,${raw}`;
  };

  const formatTimestamp = (ts: string | undefined) => {
    if (!ts) return '';
    try {
      if (ts.includes('T') && (ts.endsWith('Z') || ts.includes('+'))) {
        return new Date(ts).toLocaleString();
      }
      return ts;
    } catch {
      return ts;
    }
  };

  const brandLookup = useMemo(() => new Map(brands.map((b) => [b.id, b.name])), [brands]);
  const resolveBrandName = (sub: SubmissionData) => {
    const explicit = (sub as any).brandName || (sub as any).brand?.name;
    if (explicit) return explicit;
    const listName = asArray((sub as any).brands)
      .map((b: any) => {
        if (typeof b === 'string') return brandLookup.get(b) || b;
        return brandLookup.get(b?.id) || b?.name || b?.id;
      })
      .find(Boolean);
    if (listName) return listName as string;
    const id = (sub as any).brandId;
    if (id) return brandLookup.get(id) || id;
    return 'Unknown brand';
  };
  const renderBrandChip = (sub: SubmissionData) => <BrandChip brandName={resolveBrandName(sub)} />;

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (previewAttachment) {
          setPreviewAttachment(null);
        } else if (isDrawerOpen) {
          setIsDrawerOpen(false);
          setSelectedSubmissionId(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen, previewAttachment]);

  // --- STAGE LOGIC ---
  const stagesOrder = [WorkflowStage.F_AND_B, WorkflowStage.F_AND_P, WorkflowStage.DIGITAL_SALES, WorkflowStage.COMPLETED];

  const getNextStage = (current: WorkflowStage): WorkflowStage => {
      const idx = stagesOrder.indexOf(current);
      return idx < stagesOrder.length - 1 ? stagesOrder[idx + 1] : WorkflowStage.COMPLETED;
  };

  const handleStageAction = async (id: string, action: 'Approve' | 'Reject' | 'Return') => {
      if (actionLoadingId) return;
      const submission = submissions.find(s => s.submissionId === id);
      if (!submission) return;

      const currentStage = submission.currentStage || WorkflowStage.F_AND_B;
      let nextStage = currentStage;
      let nextStatus = submission.status || SubmissionStatus.PENDING;
      let logAction = action;
      let logComment = '';
      const note = returnNote.trim();

      if (action === 'Approve') {
          nextStage = getNextStage(currentStage as WorkflowStage);
          if (nextStage === WorkflowStage.COMPLETED) {
              nextStatus = SubmissionStatus.APPROVED;
          }
          logComment = `Approved by ${currentStage}`;
      } else if (action === 'Reject') {
          nextStatus = SubmissionStatus.REJECTED;
          logComment = `Rejected at ${currentStage}`;
      } else if (action === 'Return') {
          nextStatus = SubmissionStatus.NEEDS_REVISION;
          nextStage = WorkflowStage.RETURNED;
          logComment = note || 'Returned for revision';
      }

      // Create Log Entry
      const logTimestampIso = new Date().toISOString();
      const newLog: WorkflowLog = {
          id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Date.now().toString(),
          stage: currentStage,
          action: logAction,
          comment: logComment,
          user: currentUser.name,
          timestamp: new Date(logTimestampIso).toLocaleString()
      };

      const updated = submissions.map(s => 
          s.submissionId === id 
            ? { ...s, status: nextStatus, currentStage: nextStage, workflowLogs: [...(s.workflowLogs || []), newLog] } 
            : s
      );

      setActionLoadingId(id);
      onUpdateSubmissions(updated);
      
      try {
          await apiClient.submissions.updateStatus(id, {
              status: nextStatus,
              currentStage: nextStage,
              workflowLog: {
                id: newLog.id,
                stage: currentStage,
                action: logAction,
                comment: logComment,
                user: currentUser.name,
                timestamp: logTimestampIso,
              },
          });

          let notifTitle = `Submission ${action}d`;
          if (action === 'Return') notifTitle = 'Returned to Manager';

          onAddNotification(
              action === 'Approve' ? 'success' : action === 'Return' ? 'warning' : 'error',
              notifTitle,
              action === 'Return' ? `Sent back for: ${logComment}` : `Moved from ${currentStage} to ${nextStage}`,
              AdminView.SUBMISSIONS,
              id
          );
          if (showToast) {
             showToast(
                action === 'Approve' ? 'success' : action === 'Return' ? 'warning' : 'error',
                notifTitle,
                action === 'Return' ? `Sent back for: ${logComment}` : `Moved from ${currentStage} to ${nextStage}`
             );
          }
          
          if (action === 'Return' && note) {
            try {
              await apiClient.submissions.comment(id, {
                comment: note,
                stage: currentStage,
                user: currentUser.name,
              });
            } catch (commentError) {
              console.warn('Failed to persist return note as comment', commentError);
            }
          }
          await refreshWorkflowLogs(id);
      } catch (error) {
          console.error('Failed to update submission', error);
          onAddNotification('error', 'Update Failed', 'Unable to update submission.');
          if (showToast) showToast('error', 'Update Failed', 'Unable to update submission.');
      } finally {
          setActionLoadingId(null);
          setConfirmingAction(null);
          setReturnNote('');
      }
  };

  const refreshWorkflowLogs = useCallback(async (submissionId: string) => {
    try {
      const logs = await apiClient.submissions.logs(submissionId);
      const normalized: WorkflowLog[] = (logs || []).map((log) => ({
        ...log,
        timestamp: log.timestamp ? new Date(log.timestamp).toLocaleString() : new Date().toLocaleString(),
      }));

      const updatedList = submissionsRef.current.map((s) =>
        s.submissionId === submissionId ? { ...s, workflowLogs: normalized } : s
      );
      onUpdateSubmissions(updatedList);
    } catch (error) {
      console.error('Failed to refresh logs', error);
    }
  }, [onUpdateSubmissions]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPendingAttachments(prev => [...prev, ...Array.from(e.target.files || [])]);
    }
  };

  const removeAttachment = (index: number) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDiscussItem = (itemName: string) => {
    setDiscussingItem({ name: itemName });
    setActiveTab('timeline');
    setTimeout(() => {
        // Focus the chat input
        const input = document.querySelector('input[placeholder^="Comment on"], input[placeholder="Add a note..."]') as HTMLInputElement;
        if (input) input.focus();
    }, 100);
  };

  const handlePostComment = async (id: string) => {
    if ((!commentText.trim() && pendingAttachments.length === 0) || isUploading) return;

    const submission = submissions.find(s => s.submissionId === id);
    if (!submission) return;

    let logStage = submission.currentStage || WorkflowStage.F_AND_B;
    // Determine stage based on user department if needed, similar to original view
    if (currentUser.department === Department.FB) logStage = WorkflowStage.F_AND_B;
    else if (currentUser.department === Department.FP) logStage = WorkflowStage.F_AND_P;
    else if (currentUser.department === Department.DS) logStage = WorkflowStage.DIGITAL_SALES;

    setIsUploading(true);

    try {
      const uploadedAttachments: Attachment[] = [];
      
      // Upload files first
      for (const file of pendingAttachments) {
        const res = await apiClient.uploads.uploadImage(file);
        // Assuming res returns { key: string, url: string } or similar. 
        // The current uploadImage returns whatever the backend returns.
        // Based on uploadImageToR2 in functions, it returns { key, url, ... }
        
        const type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file';
        uploadedAttachments.push({
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          type,
          url: res.url || `/api/assets?key=${encodeURIComponent(res.key)}`, // Fallback if url not direct
          size: file.size
        });
      }

      const finalComment = discussingItem 
        ? `Regarding: ${discussingItem.name} - ${commentText}`
        : commentText;

      await apiClient.submissions.comment(id, {
        comment: finalComment,
        stage: logStage,
        user: currentUser.name,
        attachments: uploadedAttachments
      });

      setCommentText('');
      setPendingAttachments([]);
      setDiscussingItem(null);
      await refreshWorkflowLogs(id);
      onAddNotification('success', 'Comment Posted', 'Your comment has been added.');
      if (showToast) showToast('success', 'Comment Posted', 'Your comment has been added.');
    } catch (error) {
      console.error('Failed to post comment', error);
      onAddNotification('error', 'Post Failed', 'Could not post comment.');
      if (showToast) showToast('error', 'Post Failed', 'Could not post comment.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadExcel = async (data: SubmissionData) => {
    const ExcelJS = (window as any).ExcelJS;
    if (!ExcelJS) {
        onAddNotification('error', 'Library Missing', 'Excel generator not fully loaded yet.');
        if (showToast) showToast('error', 'Library Missing', 'Excel generator not fully loaded yet.');
        return;
    }

    try {
        onAddNotification('info', 'Generating Report', 'Creating Excel file...');
        if (showToast) showToast('info', 'Generating Report', 'Creating Excel file...');
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Submission Data');

        sheet.columns = [
            { key: 'id', width: 10 }, 
            { key: 'name', width: 35 }, 
            { key: 'sprice', width: 15 }, 
            { key: 'desc', width: 40 }, 
            { key: 'platforms', width: 40 }, 
            { key: 'image', width: 30 },
        ];

        // ... (Reuse Excel generation logic from SubmissionsView)
        // For brevity, I'll simplify or assume the logic is similar
        // Adding basic rows
        sheet.addRow(['Submission ID', data.submissionId]);
        sheet.addRow(['Manager', data.manager.name]);
        sheet.addRow([]);
        sheet.addRow(['Item Name', 'Price', 'Description', 'Platforms']);
        
        data.items.forEach(item => {
            sheet.addRow([item.name, item.sellingPrice, item.description, item.selectedAggregators.join(', ')]);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Submission_${data.submissionId}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        onAddNotification('success', 'Download Complete', 'Report downloaded.');
        if (showToast) showToast('success', 'Download Complete', 'Report downloaded.');
    } catch (error) {
        onAddNotification('error', 'Export Failed', 'Error generating Excel.');
        if (showToast) showToast('error', 'Export Failed', 'Error generating Excel.');
    }
  };

  const filteredSubmissions = submissions.filter(s => {
      // Filter for Campaigns only (or undefined which defaults to Campaign for legacy)
      const isCampaign = !s.type || s.type === SubmissionType.CAMPAIGN;
      const matchStatus = statusFilter === 'All Statuses' || s.status === statusFilter;
      const matchBrand = brandFilter === 'All Brands' || s.brands.includes(brandFilter);
      const term = searchTerm.toLowerCase();
      const matchSearch = !term || 
        s.submissionId?.toLowerCase().includes(term) || 
        s.manager.name.toLowerCase().includes(term);
      
      // Tab Logic
      let matchesTab = true;
      if (statusTab === 'active') {
          matchesTab = ![SubmissionStatus.APPROVED, SubmissionStatus.REJECTED].includes(s.status);
      } else if (statusTab === 'completed') {
          matchesTab = s.status === SubmissionStatus.APPROVED;
      } else if (statusTab === 'rejected') {
          matchesTab = s.status === SubmissionStatus.REJECTED;
      }

      return isCampaign && matchStatus && matchBrand && matchSearch && matchesTab;
  }).sort((a, b) => {
      return new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime();
  });

  const selectedSubmission = submissions.find(s => s.submissionId === selectedSubmissionId);

  // --- Render Helpers ---

  const getStageColor = (stage: string) => {
    switch (stage) {
      case WorkflowStage.F_AND_B: return 'border-orange-200 bg-orange-50/50';
      case WorkflowStage.F_AND_P: return 'border-indigo-200 bg-indigo-50/50';
      case WorkflowStage.DIGITAL_SALES: return 'border-teal-200 bg-teal-50/50';
      case WorkflowStage.COMPLETED: return 'border-emerald-200 bg-emerald-50/50';
      default: return 'border-zinc-200 bg-zinc-50/50';
    }
  };

  const renderAttachmentPreview = (att: Attachment) => {
    if (att.type === 'image') {
      return (
        <div key={att.id} className="relative group cursor-pointer" onClick={() => setPreviewAttachment(att)}>
          <img src={att.url} alt={att.name} className="w-20 h-20 object-cover rounded-lg border border-zinc-200" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg" />
        </div>
      );
    }
    if (att.type === 'video') {
      return (
        <div key={att.id} className="relative group cursor-pointer w-20 h-20 bg-zinc-100 rounded-lg border border-zinc-200 flex items-center justify-center" onClick={() => setPreviewAttachment(att)}>
          <Play className="w-8 h-8 text-zinc-400" />
        </div>
      );
    }
    return (
      <a key={att.id} href={att.url} target="_blank" rel="noreferrer" className="w-20 h-20 bg-zinc-50 rounded-lg border border-zinc-200 flex flex-col items-center justify-center p-2 text-center hover:bg-zinc-100">
        <FileIcon className="w-6 h-6 text-zinc-400 mb-1" />
        <span className="text-[10px] text-zinc-500 truncate w-full">{att.name}</span>
      </a>
    );
  };

  const calculateDuration = (dateStr: string | undefined) => {
      if (!dateStr) return '0h';
      const start = new Date(dateStr).getTime();
      if (isNaN(start)) return '0h';
      const now = Date.now();
      const diff = now - start;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      if (days > 0) return `${days}d ${hours}h`;
      return `${hours}h`;
  };

  const getTimeInStage = (sub: SubmissionData) => {
      if (!sub.workflowLogs || sub.workflowLogs.length === 0) {
          return { text: calculateDuration(sub.timestamp), isLong: false };
      }
      const lastStatusChange = [...sub.workflowLogs].reverse().find(l => l.action !== 'Comment');
      const startTime = lastStatusChange ? lastStatusChange.timestamp : sub.timestamp;
      
      const start = new Date(startTime || '').getTime();
      const isLong = !isNaN(start) && (Date.now() - start) > (1000 * 60 * 60 * 24 * 2); // > 2 days

      return { text: calculateDuration(startTime), isLong };
  };

  return (
    <div className="h-full flex flex-col bg-zinc-50/50">
      {/* Toolbar */}
      <div className="px-6 py-4 border-b border-zinc-200 bg-white flex justify-between items-center shrink-0">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-zinc-900">Submissions</h2>
          
          {/* Added Tabs */}
          <div className="flex bg-zinc-100 p-1 rounded-lg">
              {['active', 'completed', 'rejected'].map((tab) => (
                  <button
                      key={tab}
                      onClick={() => setStatusTab(tab as any)}
                      className={`px-3 py-1.5 rounded-md text-xs font-bold capitalize transition-all ${
                          statusTab === tab ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                      }`}
                  >
                      {tab}
                  </button>
              ))}
          </div>

          <div className="flex bg-zinc-100 p-1 rounded-lg">
            <button 
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              <List className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setViewMode('board')}
              className={`p-1.5 rounded-md transition-all ${viewMode === 'board' ? 'bg-white shadow text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
                     {onBack && (
                         <button
                             onClick={onBack}
                             className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm font-bold text-zinc-700 hover:bg-zinc-50 shadow-sm"
                         >
                             <CornerUpLeft className="w-4 h-4" />
                             Back
                         </button>
                     )}
           <div className="relative">
              <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none w-64"
              />
           </div>
           {onNavigate && (
             <div className="w-1"></div>
           )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden relative">
        {viewMode === 'list' ? (
          <div className="h-full overflow-y-auto p-6">
            <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="bg-zinc-50 text-zinc-500 font-bold uppercase text-xs border-b border-zinc-200">
                  <tr>
                    <th className="px-6 py-4">ID</th>
                    <th className="px-6 py-4">Manager</th>
                    <th className="px-6 py-4">Brands</th>
                    <th className="px-6 py-4">Received</th>
                    <th className="px-6 py-4">Stage</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filteredSubmissions.map(sub => (
                    <tr key={sub.submissionId} onClick={() => { setSelectedSubmissionId(sub.submissionId || null); setIsDrawerOpen(true); }} className="hover:bg-zinc-50 cursor-pointer transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <SubmissionCode submissionId={sub.submissionId} brandName={resolveBrandName(sub)} />
                          {renderBrandChip(sub)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-zinc-600">{sub.manager.name}</td>
                      <td className="px-6 py-4 text-zinc-600">{sub.brands.join(', ')}</td>
                      <td className="px-6 py-4 text-zinc-500 text-xs">{formatTimestamp(sub.timestamp)}</td>
                      <td className="px-6 py-4"><span className="px-2 py-1 bg-zinc-100 rounded text-xs font-bold text-zinc-600">{sub.currentStage}</span></td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold 
                          ${sub.status === 'Approved' ? 'bg-emerald-100 text-emerald-700' : 
                            sub.status === 'Rejected' ? 'bg-red-100 text-red-700' : 
                            'bg-amber-100 text-amber-700'}`}>
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button className="text-teal-600 hover:text-teal-700 font-bold text-xs">View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-x-auto p-6 flex gap-6 bg-white/40 backdrop-blur-xl">
            {[WorkflowStage.F_AND_B, WorkflowStage.F_AND_P, WorkflowStage.DIGITAL_SALES, WorkflowStage.COMPLETED].map(stage => (
              <div key={stage} className="min-w-[300px] w-72 flex flex-col h-full">
                <div className={`flex items-center justify-between mb-4 px-3 py-2 border-l-4 ${getStageColor(stage)} rounded-r-lg`}>
                  <h3 className="font-bold text-zinc-700 text-sm">{stage}</h3>
                  <span className="bg-white/50 text-zinc-600 px-2 py-0.5 rounded-full text-xs font-bold border border-zinc-200/50">
                    {filteredSubmissions.filter(s => s.currentStage === stage).length}
                  </span>
                </div>
                <div className="flex-1 rounded-xl space-y-3 overflow-y-auto custom-scrollbar pb-4">
                  {filteredSubmissions.filter(s => s.currentStage === stage).map(sub => (
                    <div 
                      key={sub.submissionId}
                      onClick={() => { setSelectedSubmissionId(sub.submissionId || null); setIsDrawerOpen(true); }}
                      className="group bg-white rounded-xl border border-zinc-200 shadow-sm hover:shadow-md hover:border-teal-400 transition-all cursor-pointer overflow-hidden"
                    >
                      {/* Image Area */}
                      <div className="aspect-[16/10] bg-zinc-100 relative overflow-hidden">
                         {(() => {
                             const src = resolveImageUrl(sub.items[0]?.image);
                             return src ? (
                                <img src={src} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                             ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-300">
                                    <ImageIcon className="w-8 h-8" />
                                </div>
                             );
                         })()}
                         <div className="absolute top-2 right-2 backdrop-blur-md bg-white/90 px-2 py-1 rounded-lg text-[10px] font-bold border border-white/50 shadow-sm">
                            {sub.status}
                         </div>
                      </div>

                      <div className="p-4">
                        <div className="flex justify-between items-start mb-2">
                            <span className="font-mono text-xs font-bold text-zinc-400">
                              <SubmissionCode submissionId={sub.submissionId} brandName={resolveBrandName(sub)} />
                            </span>
                            {renderBrandChip(sub)}
                        </div>
                        <h4 className="font-bold text-zinc-900 text-sm mb-1 truncate">{sub.manager.name}</h4>
                        <p className="text-xs text-zinc-500 mb-3 truncate">{sub.brands.join(', ')}</p>
                        <div className="flex items-center justify-between text-xs text-zinc-400 border-t border-zinc-50 pt-3">
                            <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(sub.timestamp || '').toLocaleDateString()}</div>
                            <div className="flex items-center gap-1"><List className="w-3 h-3" /> {sub.items.length} Items</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Drawer */}
      {isDrawerOpen && selectedSubmission && (
        <>
            {/* Backdrop Overlay */}
            <div 
                className="absolute inset-0 bg-black/20 backdrop-blur-sm z-10 transition-opacity" 
                onClick={() => setIsDrawerOpen(false)}
            />
            
            <div className="absolute inset-y-0 right-0 w-full md:w-[900px] bg-white shadow-2xl border-l border-zinc-200 z-20 flex flex-col animate-fade-in-right">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 px-6 py-4 border-b border-zinc-100 flex justify-between items-center bg-white/80 backdrop-blur-md">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-bold text-zinc-900">
                        <SubmissionCode submissionId={selectedSubmission.submissionId} brandName={resolveBrandName(selectedSubmission)} />
                      </h3>
                      {renderBrandChip(selectedSubmission)}
                    </div>
                    <p className="text-xs text-zinc-500">{selectedSubmission.manager.name} • {selectedSubmission.brands.join(', ')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex bg-zinc-100 rounded-lg p-0.5 mr-2">
                        <button 
                            onClick={() => setItemsViewMode('list')}
                            className={`p-1.5 rounded-md transition-all ${itemsViewMode === 'list' ? 'bg-white shadow text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
                            title="List View"
                        >
                            <List className="w-4 h-4" />
                        </button>
                        <button 
                            onClick={() => setItemsViewMode('gallery')}
                            className={`p-1.5 rounded-md transition-all ${itemsViewMode === 'gallery' ? 'bg-white shadow text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
                            title="Gallery View"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                    </div>
                    <button onClick={() => handleDownloadExcel(selectedSubmission)} className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-500"><Download className="w-5 h-5" /></button>
                    <button onClick={() => setIsDrawerOpen(false)} className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-500"><X className="w-5 h-5" /></button>
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
             {/* Left: Manifest (Items) */}
             <div className="w-[55%] overflow-y-auto p-6 border-r border-zinc-100 bg-zinc-50/30">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <Box className="w-4 h-4 text-teal-500" />
                        <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider">CAMPAIGN ITEMS</h4>
                    </div>
                </div>
                
                {itemsViewMode === 'list' ? (
                    <div className="space-y-4">
                        {selectedSubmission.items.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex gap-4">
                                <div className="w-24 h-24 bg-zinc-100 rounded-lg border border-zinc-200 flex items-center justify-center shrink-0 overflow-hidden group cursor-pointer">
                                    {(() => {
                                        const src = resolveImageUrl(item.image);
                                        return src ? (
                                            <img src={src} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        ) : (
                                            <ImageIcon className="w-8 h-8 text-zinc-300" />
                                        );
                                    })()}
                                </div>
                                <div className="flex-1 min-w-0 flex flex-col">
                                    <div className="flex justify-between items-start mb-1">
                                        <h4 className="font-bold text-zinc-900 text-sm truncate pr-2">{item.name}</h4>
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleDiscussItem(item.name); }}
                                                className={`p-1.5 rounded-lg transition-all ${discussingItem?.name === item.name ? 'bg-teal-500 text-white shadow-md shadow-teal-200' : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600'}`}
                                                title="Discuss this item"
                                            >
                                                <MessageSquare className="w-3.5 h-3.5" />
                                            </button>
                                            <span className="text-teal-600 font-mono text-xs font-bold bg-teal-50 px-2 py-1 rounded border border-teal-100">{currency} {item.sellingPrice}</span>
                                        </div>
                                    </div>
                                    <p className="text-zinc-500 text-xs leading-relaxed line-clamp-2 mb-3 flex-1">{item.description}</p>
                                    <div className="flex flex-wrap gap-2 mt-auto">
                                        {item.selectedAggregators.map(agg => (
                                            <span key={agg} className="px-3 py-1 bg-zinc-50 border border-zinc-200 rounded text-[10px] font-bold text-zinc-600 shadow-sm">{agg}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        {selectedSubmission.items.map((item, idx) => (
                            <div key={idx} className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                                {/* Image */}
                                <div className="aspect-[4/3] bg-zinc-100 relative border-b border-zinc-100">
                                    {(() => {
                                        const src = resolveImageUrl(item.image);
                                        return src ? (
                                            <img src={src} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-zinc-300"><ImageIcon className="w-8 h-8" /></div>
                                        );
                                    })()}
                                </div>
                                
                                {/* Content */}
                                <div className="p-3 flex flex-col flex-1">
                                    <div className="flex justify-between items-start mb-2 gap-2">
                                        <h4 className="font-bold text-zinc-900 text-sm line-clamp-2 leading-tight">{item.name}</h4>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleDiscussItem(item.name); }}
                                                className={`p-1.5 rounded-lg transition-all ${discussingItem?.name === item.name ? 'bg-teal-500 text-white shadow-md shadow-teal-200' : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600'}`}
                                                title="Discuss this item"
                                            >
                                                <MessageSquare className="w-3.5 h-3.5" />
                                            </button>
                                            <span className="bg-teal-50 text-teal-700 text-[10px] font-bold px-1.5 py-0.5 rounded border border-teal-100 whitespace-nowrap">
                                                {currency} {item.sellingPrice}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <p className="text-zinc-500 text-xs line-clamp-2 mb-3 flex-1 leading-relaxed">
                                        {item.description}
                                    </p>
                                    
                                    <div className="flex flex-wrap gap-1.5 mt-auto">
                                        {item.selectedAggregators.map(agg => (
                                            <span key={agg} className="px-2 py-1 bg-white border border-zinc-200 rounded text-[10px] font-bold text-zinc-600 shadow-sm">
                                                {agg}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
             </div>

             {/* Right: Context (Chat & Logs) */}
             <div className="w-[45%] flex flex-col bg-white h-full relative border-l border-zinc-200">
                {/* Tabs Header */}
                <div className="flex items-center border-b border-zinc-200 bg-white shrink-0">
                    <button 
                        onClick={() => setActiveTab('timeline')}
                        className={`flex-1 py-4 text-xs font-bold border-b-2 transition-colors uppercase tracking-wide ${activeTab === 'timeline' ? 'border-teal-500 text-teal-600' : 'border-transparent text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`}
                    >
                        TIMELINE & REVIEW
                    </button>
                    <button 
                        onClick={() => setActiveTab('overview')}
                        className={`flex-1 py-4 text-xs font-bold border-b-2 transition-colors uppercase tracking-wide ${activeTab === 'overview' ? 'border-teal-500 text-teal-600' : 'border-transparent text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`}
                    >
                        OVERVIEW
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-zinc-50/50">
                    {activeTab === 'timeline' ? (
                        <div className="space-y-6 animate-fade-in">
                            {/* Status Card */}
                            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">CURRENT STATUS</h4>
                                        <div className="text-xl font-bold text-teal-700">{selectedSubmission.currentStage || 'F&B'}</div>
                                    </div>
                                    {(() => {
                                        const { text, isLong } = getTimeInStage(selectedSubmission);
                                        return (
                                            <div className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 border shadow-sm transition-colors
                                                ${isLong ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-teal-50 text-teal-700 border-teal-200'}`}>
                                                <Clock className={`w-3.5 h-3.5 ${isLong ? 'text-amber-500' : 'text-teal-500'}`} />
                                                <span>In Stage: {text}</span>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Visual Stepper */}
                                <div className="relative px-2 mb-2">
                                    <div className="flex items-center justify-between relative z-10">
                                        {stagesOrder.map((stage, idx) => {
                                            const currentIdx = stagesOrder.indexOf(selectedSubmission.currentStage as WorkflowStage);
                                            const isCompleted = idx < currentIdx;
                                            const isCurrent = idx === currentIdx;
                                            
                                            return (
                                                <div key={stage} className="flex flex-col items-center">
                                                    <div className={`w-4 h-4 rounded-full border-2 transition-all bg-white
                                                        ${isCompleted ? 'border-teal-500 bg-teal-500' : 
                                                          isCurrent ? 'border-teal-500 ring-4 ring-teal-50' : 
                                                          'border-zinc-200'}
                                                    `}>
                                                    </div>
                                                    <span className={`text-[10px] font-bold mt-2 ${isCurrent ? 'text-zinc-900' : 'text-zinc-400'}`}>
                                                        {stage === WorkflowStage.F_AND_B ? 'F&B' : stage === WorkflowStage.F_AND_P ? 'F&P' : stage === WorkflowStage.DIGITAL_SALES ? 'SALES' : 'DONE'}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Connector Line */}
                                    <div className="absolute top-2 left-2 right-2 h-0.5 bg-zinc-100 -z-0">
                                        <div 
                                            className="h-full bg-teal-500 transition-all duration-500"
                                            style={{ width: `${(stagesOrder.indexOf(selectedSubmission.currentStage as WorkflowStage) / (stagesOrder.length - 1)) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Logs */}
                            <div className="space-y-4">
                                {selectedSubmission.workflowLogs?.map(log => (
                                    <div key={log.id} className="flex gap-3">
                                        {log.action === 'Comment' ? (
                                            <>
                                                <div className="w-8 h-8 rounded-full bg-white border border-teal-200 text-teal-700 flex items-center justify-center shrink-0 text-xs font-bold shadow-sm">
                                                    {log.user.charAt(0)}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-baseline justify-between mb-1 px-1">
                                                        <span className="font-bold text-zinc-900 text-xs">{log.user}</span>
                                                        <span className="text-[10px] text-zinc-400">{formatTimestamp(log.timestamp)}</span>
                                                    </div>
                                                    <div className="bg-white p-3 rounded-xl rounded-tl-none border border-zinc-200 shadow-sm text-sm text-zinc-600 leading-relaxed">
                                                        {(() => {
                                                            const regardingMatch = log.comment.match(/^Regarding: (.*?) - (.*)/s);
                                                            if (regardingMatch) {
                                                                const [_, itemName, actualComment] = regardingMatch;
                                                                return (
                                                                    <>
                                                                        <div className="mb-2">
                                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-teal-50 border border-teal-100 text-teal-700 text-[10px] font-bold uppercase tracking-wide">
                                                                                <Tag className="w-3 h-3" />
                                                                                {itemName}
                                                                            </span>
                                                                        </div>
                                                                        {actualComment}
                                                                    </>
                                                                );
                                                            }
                                                            return log.comment;
                                                        })()}
                                                        {log.attachments && log.attachments.length > 0 && (
                                                            <div className="mt-3 grid grid-cols-2 gap-2">
                                                            {log.attachments.map(att => renderAttachmentPreview(att))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            (() => {
                                                let styles = {
                                                    bg: 'bg-zinc-50',
                                                    text: 'text-zinc-500',
                                                    border: 'border-zinc-100',
                                                    dot: 'bg-zinc-300'
                                                };
                                                
                                                if (log.action === 'Approve') {
                                                    styles = { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' };
                                                } else if (log.action === 'Reject') {
                                                    styles = { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' };
                                                } else if (log.action === 'Return') {
                                                    styles = { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' };
                                                }

                                                return (
                                                    <div className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg border ${styles.bg} ${styles.border} mb-1`}>
                                                        <div className="w-8 flex justify-center shrink-0">
                                                            <div className={`w-2 h-2 rounded-full ${styles.dot}`}></div>
                                                        </div>
                                                        <div className={`text-xs ${styles.text} flex-1 font-medium`}>
                                                            <span className="font-bold opacity-80 mr-1">{log.user}</span> 
                                                            <span className="uppercase text-[10px] font-bold opacity-70 mr-1 border border-current px-1 rounded">{log.action}</span>
                                                            {log.comment}
                                                        </div>
                                                        <div className={`text-[10px] opacity-60 ${styles.text} shrink-0`}>
                                                            {formatTimestamp(log.timestamp)}
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6 animate-fade-in">
                            {/* Key Dates Card */}
                            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                                <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-4">KEY DATES</h4>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <div className="text-sm text-zinc-500">Submission Received</div>
                                        <div className="text-sm font-bold text-zinc-900">{formatTimestamp(selectedSubmission.timestamp)}</div>
                                    </div>
                                    {(() => {
                                        const campaign = campaigns?.find(c => c.id === selectedSubmission.campaignId || c.name === selectedSubmission.campaignName);
                                        return (
                                            <>
                                                <div className="flex justify-between items-center">
                                                    <div className="text-sm text-zinc-500">Campaign Start</div>
                                                    <div className="text-sm font-bold text-zinc-900">{campaign?.startDate ? new Date(campaign.startDate).toLocaleDateString() : 'N/A'}</div>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <div className="text-sm text-zinc-500">Campaign End</div>
                                                    <div className="text-sm font-bold text-zinc-900">{campaign?.endDate ? new Date(campaign.endDate).toLocaleDateString() : 'N/A'}</div>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Manager Details Card */}
                            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                                <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-4">MANAGER DETAILS</h4>
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 font-bold text-lg">
                                        {selectedSubmission.manager.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-zinc-900 text-lg">{selectedSubmission.manager.name}</h3>
                                        <p className="text-zinc-500 text-sm">ID: {selectedSubmission.managerId || 'EMP001'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Summary Metrics Card */}
                            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                                <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-4">SUMMARY METRICS</h4>
                                <div className="grid grid-cols-2 gap-8">
                                    <div>
                                        <div className="text-xs text-zinc-500 mb-1">Total Items</div>
                                        <div className="text-2xl font-bold text-zinc-900">{selectedSubmission.items.length}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-zinc-500 mb-1">Platform Count</div>
                                        <div className="text-2xl font-bold text-zinc-900">
                                            {Array.from(new Set(selectedSubmission.items.flatMap(i => i.selectedAggregators))).length}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom Action Bar (Chat + Buttons) */}
                <div className="p-4 bg-white border-t border-zinc-200 shrink-0">
                    {/* Chat Input */}
                    <div className="mb-4 relative">
                        {/* Context Banner */}
                        {discussingItem && (
                            <div className="absolute bottom-full left-0 right-0 mb-2 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 flex items-center justify-between animate-fade-in-up shadow-sm z-10">
                                <div className="flex items-center gap-2 text-teal-800 text-xs font-bold">
                                    <Tag className="w-3.5 h-3.5" />
                                    <span>Regarding: {discussingItem.name}</span>
                                </div>
                                <button 
                                    onClick={() => setDiscussingItem(null)}
                                    className="text-teal-400 hover:text-teal-700 transition-colors"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        {pendingAttachments.length > 0 && (
                            <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
                            {pendingAttachments.map((file, i) => (
                                <div key={i} className="relative w-16 h-16 bg-zinc-50 rounded-lg border border-zinc-200 flex items-center justify-center shrink-0">
                                <span className="text-[10px] text-zinc-500 truncate px-1">{file.name}</span>
                                <button onClick={() => removeAttachment(i)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                                </div>
                            ))}
                            </div>
                        )}
                        <div className="relative">
                            <input 
                                type="text" 
                                value={commentText}
                                onChange={e => setCommentText(e.target.value)}
                                placeholder={discussingItem ? `Comment on ${discussingItem.name}...` : "Add a note..."}
                                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 pl-10 pr-12 text-sm focus:ring-2 focus:ring-teal-100 focus:bg-white outline-none transition-all"
                                onKeyDown={e => e.key === 'Enter' && handlePostComment(selectedSubmission.submissionId!)}
                            />
                            
                            {/* Left Action: Attach */}
                            <button 
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
                            >
                                <Paperclip className="w-4 h-4" />
                            </button>

                            {/* Right Action: Send */}
                            <button 
                                onClick={() => handlePostComment(selectedSubmission.submissionId!)}
                                disabled={isUploading || (!commentText.trim() && pendingAttachments.length === 0)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-700 disabled:opacity-50 disabled:hover:bg-zinc-900 transition-colors shadow-sm"
                            >
                                {isUploading ? <Clock className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </button>
                            <input type="file" multiple ref={fileInputRef} className="hidden" onChange={handleFileSelect} />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    {(() => {
                        const isMyStage = (
                            currentUser.role === UserRole.ADMIN ||
                            (currentUser.department === Department.FB && selectedSubmission.currentStage === WorkflowStage.F_AND_B) ||
                            (currentUser.department === Department.FP && selectedSubmission.currentStage === WorkflowStage.F_AND_P) ||
                            (currentUser.department === Department.DS && selectedSubmission.currentStage === WorkflowStage.DIGITAL_SALES)
                        );
                        const canAct = isMyStage && 
                                       selectedSubmission.status !== SubmissionStatus.APPROVED && 
                                       selectedSubmission.status !== SubmissionStatus.REJECTED &&
                                       selectedSubmission.status !== SubmissionStatus.NEEDS_REVISION;

                        if (!canAct) return null;

                        return (
                            <div>
                                {confirmingAction ? (
                                    <div className="bg-zinc-900 p-4 rounded-xl animate-fade-in">
                                        <h4 className="text-white font-bold text-sm mb-2">Confirm {confirmingAction.action}?</h4>
                                        {confirmingAction.action === 'Return' && (
                                            <textarea
                                                value={returnNote}
                                                onChange={(e) => setReturnNote(e.target.value)}
                                                placeholder="Reason for return..."
                                                className="w-full bg-zinc-800 text-white text-xs p-3 rounded-lg mb-3 outline-none border border-zinc-700 focus:border-zinc-500"
                                                rows={2}
                                            />
                                        )}
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleStageAction(selectedSubmission.submissionId!, confirmingAction.action)}
                                                disabled={!!actionLoadingId}
                                                className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold text-zinc-900 transition-colors
                                                    ${confirmingAction.action === 'Approve' ? 'bg-emerald-400 hover:bg-emerald-300' : 
                                                      confirmingAction.action === 'Return' ? 'bg-amber-400 hover:bg-amber-300' : 
                                                      'bg-red-400 hover:bg-red-300'} 
                                                    ${actionLoadingId ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                {actionLoadingId ? 'Processing...' : 'Confirm'}
                                            </button>
                                            <button 
                                                onClick={() => { setConfirmingAction(null); setReturnNote(''); }} 
                                                className="flex-1 py-2 px-3 rounded-lg text-xs font-bold bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setConfirmingAction({ id: selectedSubmission.submissionId!, action: 'Approve' })}
                                            className="flex-1 flex flex-col items-center justify-center gap-1 px-4 py-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold transition-all active:scale-95"
                                        >
                                            <div className="w-5 h-5 rounded-full border-2 border-emerald-600 flex items-center justify-center"><Check className="w-3 h-3" /></div>
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => setConfirmingAction({ id: selectedSubmission.submissionId!, action: 'Return' })}
                                            className="flex-1 flex flex-col items-center justify-center gap-1 px-4 py-3 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-xl text-xs font-bold transition-all active:scale-95"
                                        >
                                            <CornerUpLeft className="w-5 h-5" />
                                            Return
                                        </button>
                                        <button
                                            onClick={() => setConfirmingAction({ id: selectedSubmission.submissionId!, action: 'Reject' })}
                                            className="flex-1 flex flex-col items-center justify-center gap-1 px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-xl text-xs font-bold transition-all active:scale-95"
                                        >
                                            <div className="w-5 h-5 rounded-full border-2 border-red-600 flex items-center justify-center"><X className="w-3 h-3" /></div>
                                            Reject
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
             </div>
          </div>
        </div>
        </>
      )}

      {/* Media Lightbox */}
      {previewAttachment && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="absolute inset-0" onClick={() => setPreviewAttachment(null)} />
          <button onClick={() => setPreviewAttachment(null)} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10"><X className="w-8 h-8" /></button>
          <div className="relative z-10 max-w-full max-h-full flex items-center justify-center">
            {previewAttachment.type === 'image' ? (
                <img src={previewAttachment.url} alt={previewAttachment.name} className="max-w-full max-h-full rounded-lg shadow-2xl" />
            ) : previewAttachment.type === 'video' ? (
                <video src={previewAttachment.url} controls autoPlay className="max-w-full max-h-full rounded-lg shadow-2xl" />
            ) : (
                <div className="bg-white p-8 rounded-2xl text-center max-w-sm w-full">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileIcon className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-xl font-bold text-zinc-900 mb-2 truncate">{previewAttachment.name}</h3>
                <p className="text-zinc-500 text-sm mb-6">This file cannot be previewed directly.</p>
                <a href={previewAttachment.url} download className="block w-full bg-zinc-900 text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-zinc-800 transition-colors">Download File</a>
                </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
